import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["paymentTermField", "shortNameField", "errorAlert", "errorList", "submitButton", "skipGcCheckbox", "gcCredentialsFields", "gcLoginField", "gcPasswordField", "gcLoginRequired", "gcPasswordRequired", "submitGcButton"]
  static values = {
    registrationId: String,
    step: Number,
    processingText: String,
    submitText: String,
    validationError: String,
    unexpectedError: String,
    connectionError: String,
    alertFallback: String,
    registrationNotFound: String,
    invalidJson: String,
    serverError: String
  }

  connect() {
    console.log("Wizard step controller connected for step:", this.stepValue)
  }

  // Auto-save field on blur/change
  async autosave(event) {
    const field = event.target
    const fieldName = field.name.replace('[]', '') // Remove [] from array fields like route_ids[]
    let fieldValue

    // Parse field value based on type
    if (field.type === 'checkbox') {
      fieldValue = field.checked
    } else if (field.multiple) {
      // Multi-select - handle both regular select and Select2
      if ($(field).hasClass('select2-hidden-accessible')) {
        // Select2 field - use Select2 API
        fieldValue = $(field).val() || []
      } else {
        // Regular multi-select
        fieldValue = Array.from(field.selectedOptions).map(option => option.value)
      }
    } else if (field.type === 'number') {
      fieldValue = field.value
    } else {
      fieldValue = field.value
    }

    console.log("Autosaving field:", fieldName, "=", fieldValue)

    try {
      const response = await fetch(
        `/client_registrations/${this.registrationIdValue}/wizard/${this.stepValue}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            field_name: fieldName,
            field_value: fieldValue
          })
        }
      )

      if (response.ok) {
        const data = await response.json()

        // Update auto-generated fields if they changed
        if (data.client_short_name && this.hasShortNameFieldTarget) {
          this.shortNameFieldTarget.value = data.client_short_name
          this.showSaved(this.shortNameFieldTarget)
        }

        this.showSaved(field)
      } else {
        const errorData = await response.json()
        console.error("Autosave failed:", errorData)
        this.showError(field, errorData.error || 'Błąd zapisu')
      }
    } catch (error) {
      console.error("Autosave error:", error)
      this.showError(field, 'Błąd połączenia')
    }
  }

  // Toggle skip GC account - disable/enable fields and update button
  toggleSkipGcAccount(event) {
    const isSkipped = event.target.checked

    // Toggle fields disabled state and opacity
    if (this.hasGcCredentialsFieldsTarget) {
      this.gcCredentialsFieldsTarget.classList.toggle('opacity-50', isSkipped)
    }

    if (this.hasGcLoginFieldTarget) {
      this.gcLoginFieldTarget.disabled = isSkipped
      if (isSkipped) {
        this.gcLoginFieldTarget.value = ''
      }
    }

    if (this.hasGcPasswordFieldTarget) {
      this.gcPasswordFieldTarget.disabled = isSkipped
      if (isSkipped) {
        this.gcPasswordFieldTarget.value = ''
      }
    }

    // Toggle required asterisks
    if (this.hasGcLoginRequiredTarget) {
      this.gcLoginRequiredTarget.classList.toggle('d-none', isSkipped)
    }

    if (this.hasGcPasswordRequiredTarget) {
      this.gcPasswordRequiredTarget.classList.toggle('d-none', isSkipped)
    }

    // Update button text, style and confirm message
    if (this.hasSubmitGcButtonTarget) {
      const form = this.submitGcButtonTarget
      const button = form.querySelector('button') || form

      // Get text from data attributes
      const skipText = form.dataset.skipText
      const normalText = form.dataset.normalText
      const skipConfirm = form.dataset.skipConfirm
      const normalConfirm = form.dataset.normalConfirm

      // Update button text
      button.textContent = isSkipped ? skipText : normalText

      // Update button style
      button.classList.toggle('btn-warning', isSkipped)
      button.classList.toggle('btn-success', !isSkipped)

      // Update confirm message
      form.dataset.confirm = isSkipped ? skipConfirm : normalConfirm
    }
  }

  // Toggle payment term field visibility based on payment method
  togglePaymentTerm(event) {
    const paymentMethod = event.target.value
    const requiresTermMethods = ['cash_with_term', 'transfer_with_term', 'partial_cash_transfer']

    if (this.hasPaymentTermFieldTarget) {
      if (requiresTermMethods.includes(paymentMethod)) {
        this.paymentTermFieldTarget.style.display = 'block'
      } else {
        this.paymentTermFieldTarget.style.display = 'none'
      }
    }
  }

  // Show save indicator
  showSaved(field) {
    const indicator = this.findOrCreateIndicator(field, 'save')

    indicator.style.display = 'block'
    indicator.classList.remove('text-danger')
    indicator.classList.add('text-success')
    indicator.innerHTML = '<i class="bi bi-check-circle"></i> Zapisano'

    // Hide after 2 seconds
    setTimeout(() => {
      indicator.style.display = 'none'
    }, 2000)
  }

  // Show error indicator
  showError(field, message) {
    const indicator = this.findOrCreateIndicator(field, 'error')

    indicator.style.display = 'block'
    indicator.classList.remove('text-success')
    indicator.classList.add('text-danger')
    indicator.innerHTML = `<i class="bi bi-x-circle"></i> ${message}`

    // Hide after 3 seconds
    setTimeout(() => {
      indicator.style.display = 'none'
    }, 3000)
  }

  // Find existing indicator or create new one
  findOrCreateIndicator(field, type) {
    // Try to find existing indicator
    let container = field.closest('.field-group') || field.closest('.form-check') || field.parentElement
    let indicator = container.querySelector('.save-indicator')

    if (!indicator) {
      // Create new indicator if it doesn't exist
      indicator = document.createElement('div')
      indicator.className = 'save-indicator text-success mt-1'
      indicator.style.display = 'none'
      container.appendChild(indicator)
    }

    return indicator
  }

  // Submit form for approval
  async submitForm(event) {
    event.preventDefault()

    // Disable button and show loading
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      const processingText = this.hasProcessingTextValue ? this.processingTextValue : 'Przetwarzanie...'
      this.submitButtonTarget.innerHTML = `<span class="spinner-border spinner-border-sm me-2"></span>${processingText}`
    }

    // Hide previous errors
    this.hideErrors()

    try {
      const response = await fetch(
        `/client_registrations/${this.registrationIdValue}/wizard/${this.stepValue}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ submit: true })
        }
      )

      if (response.ok) {
        // Success - redirect to step 3
        window.location.href = `/client_registrations/${this.registrationIdValue}/wizard/3`
      } else if (response.status === 422) {
        // Validation errors
        const data = await response.json()
        const validationError = this.hasValidationErrorValue ? this.validationErrorValue : 'Wystąpił błąd walidacji'
        this.showValidationErrors(data.errors || [validationError])

        // Re-enable button
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          const submitText = this.hasSubmitTextValue ? this.submitTextValue : 'Przyjmij zgłoszenie'
          this.submitButtonTarget.textContent = submitText
        }
      } else {
        // Other error
        const data = await response.json()
        const errorMessage = this.formatErrorMessage(data)
        this.showValidationErrors([errorMessage])

        // Re-enable button
        if (this.hasSubmitButtonTarget) {
          this.submitButtonTarget.disabled = false
          const submitText = this.hasSubmitTextValue ? this.submitTextValue : 'Przyjmij zgłoszenie'
          this.submitButtonTarget.textContent = submitText
        }
      }
    } catch (error) {
      console.error("Submit error:", error)
      const connectionError = this.hasConnectionErrorValue ? this.connectionErrorValue : 'Błąd połączenia. Sprawdź połączenie z internetem i spróbuj ponownie.'
      this.showValidationErrors([connectionError])

      // Re-enable button
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        const submitText = this.hasSubmitTextValue ? this.submitTextValue : 'Przyjmij zgłoszenie'
        this.submitButtonTarget.textContent = submitText
      }
    }
  }

  // Show validation errors
  showValidationErrors(errors) {
    if (!this.hasErrorAlertTarget || !this.hasErrorListTarget) {
      // Fallback to alert if targets not found
      alert('Błędy walidacji:\n' + errors.join('\n'))
      return
    }

    // Clear previous errors
    this.errorListTarget.innerHTML = ''

    // Add each error as list item
    errors.forEach(error => {
      const li = document.createElement('li')
      li.textContent = error
      this.errorListTarget.appendChild(li)
    })

    // Show error alert
    this.errorAlertTarget.classList.remove('d-none')

    // Scroll to top to show errors
    this.errorAlertTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  // Hide errors
  hideErrors() {
    if (this.hasErrorAlertTarget) {
      this.errorAlertTarget.classList.add('d-none')
    }
  }

  // Get CSRF token
  get csrfToken() {
    const meta = document.querySelector('[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  // Format error message based on error type
  formatErrorMessage(errorData) {
    const error = errorData.error || errorData.message || ''

    // Check if error message contains known error patterns
    if (error.includes('DocumentNotFound')) {
      return this.hasRegistrationNotFoundValue ? this.registrationNotFoundValue : 'Rejestracja nie znaleziona'
    }

    if (error.includes('JSON::ParserError')) {
      return this.hasInvalidJsonValue ? this.invalidJsonValue : 'Błąd parsowania JSON'
    }

    if (error.includes('Faraday::') || error.includes('ConnectionFailed')) {
      const connectionError = this.hasConnectionErrorValue ? this.connectionErrorValue : 'Błąd połączenia'
      return connectionError
    }

    // If error message starts with class name (e.g., "AssertionError: ..."), return as is
    if (error && error.length > 0) {
      return error
    }

    // Fallback
    return this.hasUnexpectedErrorValue ? this.unexpectedErrorValue : 'Wystąpił nieoczekiwany błąd'
  }
}
